import React, { useState } from 'react';

import {
  BubbleRow,
  CustomerBubble,
  BotBubble,
  ButtonsContainer,
  DispositionButton,
  FormattedTextWrapper,
  DebugPanelWrapper,
  TraceToggleButton,
  TraceContainer,
  LegacyTracePre,
  TraceEventWrapper,
  TraceEventHeader,
  TraceStepIcon,
  TraceStepName,
  TraceDuration,
  TraceDataBlock,
  TraceCollapsible,
  TraceCollapsibleSummary,
  TracePreBlock,
  TraceErrorText,
  TraceMutedText,
  TraceDataEntry,
} from './styled-comps';

function StructuredContent({ structured, onAction }) {
  if (!structured || !structured.buttons) return null;

  return (
    <ButtonsContainer>
      {structured.buttons.map((btn, i) => {
        const label = typeof btn === 'string' ? btn : btn.label || '';
        return (
          <DispositionButton
            key={i}
            onClick={() =>
              onAction({
                content: label,
                structured_data: {
                  selected_disposition: btn.path || btn.value,
                  action: btn.action,
                },
              })
            }
          >
            {label}
          </DispositionButton>
        );
      })}
    </ButtonsContainer>
  );
}

function FormattedText({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <FormattedTextWrapper>
      {lines.map((line, i) => {
        // Bold: **text**
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        const rendered = parts.map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
        return (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {rendered}
          </React.Fragment>
        );
      })}
    </FormattedTextWrapper>
  );
}

const STEP_STYLES = {
  route: { color: '#1a73e8', icon: '>' },
  state_loaded: { color: '#5f6368', icon: 'i' },
  ticket_transition: { color: '#e8710a', icon: '~' },
  classification: { color: '#9334e6', icon: '?' },
  disposition_matched: { color: '#1a73e8', icon: '#' },
  context_assembly: { color: '#5f6368', icon: '*' },
  kb_search: { color: '#34a853', icon: 'K' },
  system_prompt: { color: '#5f6368', icon: 'S' },
  llm_call: { color: '#ea4335', icon: 'L' },
  tool_execution: { color: '#e8710a', icon: 'T' },
  guardrail: { color: '#34a853', icon: 'G' },
  guardrail_check: { color: '#34a853', icon: 'G' },
  response_parsed: { color: '#1a73e8', icon: 'R' },
  category_set: { color: '#9334e6', icon: 'C' },
  ticket_status_update: { color: '#e8710a', icon: '~' },
  greeting_buttons: { color: '#1a73e8', icon: 'B' },
  clarifying_buttons: { color: '#1a73e8', icon: 'B' },
};

const COLLAPSIBLE_STEPS = new Set(['system_prompt', 'context_assembly']);

function TraceEventRow({ event }) {
  const style = STEP_STYLES[event.step] || { color: '#5f6368', icon: '-' };
  const isCollapsible = COLLAPSIBLE_STEPS.has(event.step);
  const hasLongText =
    event.data &&
    (event.data.text || event.data.response_text) &&
    (event.data.text || event.data.response_text || '').length > 200;

  const renderData = () => {
    if (!event.data || Object.keys(event.data).length === 0) return null;

    // For system_prompt, show collapsible full text
    if (event.step === 'system_prompt') {
      return (
        <TraceCollapsible $indented>
          <TraceCollapsibleSummary>
            System Prompt ({event.data.length} chars)
          </TraceCollapsibleSummary>
          <TracePreBlock>{event.data.text}</TracePreBlock>
        </TraceCollapsible>
      );
    }

    // For llm_call, show response preview collapsible if long
    if (event.step === 'llm_call') {
      return (
        <TraceDataBlock>
          <TraceMutedText>
            model={event.data.model} | tokens: {event.data.input_tokens} in /{' '}
            {event.data.output_tokens} out | stop: {event.data.stop_reason}
          </TraceMutedText>
          {event.data.response_text && (
            <TraceCollapsible>
              <TraceCollapsibleSummary>
                Response ({event.data.response_text.length} chars)
              </TraceCollapsibleSummary>
              <TracePreBlock>{event.data.response_text}</TracePreBlock>
            </TraceCollapsible>
          )}
        </TraceDataBlock>
      );
    }

    // For tool_execution
    if (event.step === 'tool_execution') {
      return (
        <TraceDataBlock>
          <span>
            {event.data.name}({JSON.stringify(event.data.params)})
          </span>
          {event.data.error && (
            <TraceErrorText> -- ERROR: {event.data.error}</TraceErrorText>
          )}
          {!event.data.error && event.data.result_keys && (
            <TraceMutedText> -- [{event.data.result_keys.join(', ')}]</TraceMutedText>
          )}
        </TraceDataBlock>
      );
    }

    // For classification
    if (event.step === 'classification') {
      return (
        <TraceDataBlock $muted>
          path={event.data.path} | confidence={event.data.confidence}
          {event.data.alternatives && event.data.alternatives.length > 0 && (
            <span> | alts: {event.data.alternatives.join(', ')}</span>
          )}
          <span>
            {' '}
            | tokens: {event.data.input_tokens}/{event.data.output_tokens}
          </span>
        </TraceDataBlock>
      );
    }

    // Default: render all key-value pairs
    const entries = Object.entries(event.data).filter(
      ([, v]) => v !== null && v !== undefined && v !== '',
    );
    if (entries.length === 0) return null;

    return (
      <TraceDataBlock $muted>
        {entries.map(([k, v]) => {
          const display = typeof v === 'object' ? JSON.stringify(v) : String(v);
          return (
            <TraceDataEntry key={k}>
              {k}={display}
            </TraceDataEntry>
          );
        })}
      </TraceDataBlock>
    );
  };

  return (
    <TraceEventWrapper>
      <TraceEventHeader>
        <TraceStepIcon $color={style.color}>{style.icon}</TraceStepIcon>
        <TraceStepName $color={style.color}>{event.step}</TraceStepName>
        {event.duration_ms > 0 && <TraceDuration>{event.duration_ms}ms</TraceDuration>}
      </TraceEventHeader>
      {renderData()}
    </TraceEventWrapper>
  );
}

function DebugPanel({ debug }) {
  const [open, setOpen] = useState(false);

  // Support both array (new trace format) and object (legacy)
  if (!debug) return null;

  const isTraceArray = Array.isArray(debug);
  if (isTraceArray && debug.length === 0) return null;

  return (
    <DebugPanelWrapper>
      <TraceToggleButton onClick={() => setOpen(!open)}>
        {open ? '\u25BC Hide Trace' : '\u25B6 Trace'}
      </TraceToggleButton>
      {open && (
        <TraceContainer>
          {isTraceArray ? (
            debug.map((event, i) => <TraceEventRow key={i} event={event} />)
          ) : (
            /* Legacy flat-dict fallback */
            <LegacyTracePre>{JSON.stringify(debug, null, 2)}</LegacyTracePre>
          )}
        </TraceContainer>
      )}
    </DebugPanelWrapper>
  );
}

function MessageBubble({ message, onAction }) {
  const isCustomer = message.sender === 'customer' || message.sender_type === 'CUSTOMER';

  const Bubble = isCustomer ? CustomerBubble : BotBubble;

  return (
    <BubbleRow $isCustomer={isCustomer}>
      <Bubble>
        {message.content &&
          (isCustomer ? (
            <div>{message.content}</div>
          ) : (
            <FormattedText text={message.content} />
          ))}
        {!isCustomer && message.structured && (
          <StructuredContent structured={message.structured} onAction={onAction} />
        )}
        {!isCustomer && message.debug && <DebugPanel debug={message.debug} />}
      </Bubble>
    </BubbleRow>
  );
}

export default MessageBubble;
